import {
  ChangeDetectorRef, Component, ElementRef,
  HostListener, OnDestroy, OnInit, ViewChild, DestroyRef, inject
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { FormControl } from '@angular/forms'
import { map, Observable, startWith, Subject, Subscription, takeUntil } from 'rxjs'
import { Concurso } from 'src/app/models/concurso'
import { ConcursoService } from 'src/app/services/concurso.service'
import { ConcursoDetalheStore } from './concurso-detalhe.store'
import { UnespCoreAuthService } from 'src/libs/unesp-core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

interface Tab {
  icon: string
  label: string
  route: string
  visible: boolean
  tooltip: string
}

const RECENT_KEY = 'concurso-detalhe.recentes'
const TAB_KEY_PREFIX = 'concurso-detalhe.aba.'

@Component({
  selector: 'app-concurso-detalhe',
  templateUrl: './concurso-detalhe.component.html',
  styleUrls: ['./concurso-detalhe.component.scss'],
  providers: [ConcursoDetalheStore],
})
export class ConcursoDetalheComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef)

  concurso?: Concurso
  tabs: Tab[] = []
  loading = true

  searchControl = new FormControl('')
  concursosDisponiveis: { id: number; titulo: string }[] = []
  concursosFiltrados$!: Observable<{ id: number; titulo: string }[]>
  concursosRecentes: { id: number; titulo: string }[] = []

  @ViewChild('selectorInput') selectorInput!: ElementRef<HTMLInputElement>

  private subscription = new Subscription()
  private destroy$ = new Subject<void>()
  private readonly tiposComProvas = ['RDIDP', 'RTP', 'RTC', 'PT', 'PTPP']

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private concursoService: ConcursoService,
    private store: ConcursoDetalheStore,
    private authService: UnespCoreAuthService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadRecentes()
    this.carregarConcursosDisponiveis()

    this.subscription.add(
      this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
        const id = +params['id']
        this.loading = true
        this.cdr.markForCheck()
        this.carregarConcurso(id)
      })
    )

    // Filtro local no autocomplete
    this.concursosFiltrados$ = this.searchControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const term = (typeof value === 'string' ? value : '').toLowerCase()
        const lista = !term
          ? this.concursosDisponiveis
          : this.concursosDisponiveis.filter(c =>
              c.titulo.toLowerCase().includes(term) || c.id.toString().includes(term)
            )
        return lista.sort((a, b) => a.titulo.localeCompare(b.titulo))
      })
    )
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
    this.destroy$.next()
    this.destroy$.complete()
  }

  // Atalho Ctrl+K para focar no seletor
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
      event.preventDefault()
      this.selectorInput?.nativeElement?.focus()
      this.selectorInput?.nativeElement?.select()
    }
  }

  onSelectorFocus(): void {
    this.searchControl.setValue('', { emitEvent: true })
    if (this.concursosDisponiveis.length <= 20) {
      this.concursoService.listarTodos().pipe(takeUntil(this.destroy$), takeUntilDestroyed(this.destroyRef)).subscribe(concursos => {
        this.concursosDisponiveis = concursos
        this.searchControl.setValue('', { emitEvent: true })
        this.cdr.markForCheck()
      })
    }
  }

  private carregarConcursosDisponiveis(): void {
    this.concursoService.listarTodos().pipe(takeUntil(this.destroy$), takeUntilDestroyed(this.destroyRef)).subscribe(concursos => {
      this.concursosDisponiveis = concursos
      this.cdr.markForCheck()
    })
  }

  onSelectorBlur(): void {
    setTimeout(() => {
      const current = this.searchControl.value
      if (typeof current === 'string' && current !== this.concurso?.titulo) {
        this.searchControl.setValue(this.concurso?.titulo || '', { emitEvent: false })
      }
    }, 200)
  }

  selecionarConcurso(concurso: { id: number; titulo: string }): void {
    this.saveAbaAtual()
    const abaAtual = this.getAbaAtual()
    this.addRecente(concurso)
    this.router.navigate(['/concurso', concurso.id, abaAtual])
  }

  displayFn(concurso: { id: number; titulo: string } | string): string {
    if (typeof concurso === 'string') return concurso
    return concurso?.titulo || ''
  }

  // Keyboard navigation entre abas (← →)
  onTabKeydown(event: KeyboardEvent, index: number): void {
    const visibleTabs = this.tabs.filter(t => t.visible)
    let newIndex = -1

    if (event.key === 'ArrowRight') {
      newIndex = (index + 1) % visibleTabs.length
    } else if (event.key === 'ArrowLeft') {
      newIndex = (index - 1 + visibleTabs.length) % visibleTabs.length
    }

    if (newIndex >= 0) {
      event.preventDefault()
      this.router.navigate([visibleTabs[newIndex].route], { relativeTo: this.route })
    }
  }

  private getAbaAtual(): string {
    const childRoute = this.route.firstChild
    const path = childRoute?.snapshot?.url?.[0]?.path
    return path || this.getSavedAba() || 'resumo'
  }

  private saveAbaAtual(): void {
    const aba = this.route.firstChild?.snapshot?.url?.[0]?.path
    if (aba && this.concurso?.id) {
      sessionStorage.setItem(TAB_KEY_PREFIX + this.concurso.id, aba)
    }
  }

  private getSavedAba(): string {
    if (!this.concurso?.id) return ''
    return sessionStorage.getItem(TAB_KEY_PREFIX + this.concurso.id) || ''
  }

  private addRecente(concurso: { id: number; titulo: string }): void {
    this.concursosRecentes = [concurso, ...this.concursosRecentes.filter(c => c.id !== concurso.id)].slice(0, 5)
    sessionStorage.setItem(RECENT_KEY, JSON.stringify(this.concursosRecentes))
  }

  private loadRecentes(): void {
    try {
      const stored = sessionStorage.getItem(RECENT_KEY)
      this.concursosRecentes = stored ? JSON.parse(stored) : []
    } catch {
      this.concursosRecentes = []
    }
  }

  private carregarConcurso(id: number): void {
    this.subscription.add(
      this.store.load(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(concurso => {
        this.concurso = concurso
        this.searchControl.setValue(concurso.titulo, { emitEvent: false })
        this.addRecente({ id: concurso.id as number, titulo: concurso.titulo })
        this.buildTabs()
        this.loading = false
        this.cdr.markForCheck()

        // Navegar para aba salva se estiver na rota default
        const currentChild = this.route.firstChild?.snapshot?.url?.[0]?.path
        if (currentChild === 'dados') {
          const savedAba = this.getSavedAba()
          if (savedAba && savedAba !== 'dados') {
            this.router.navigate([savedAba], { relativeTo: this.route, replaceUrl: true })
          }
        }
      })
    )
  }

  private buildTabs(): void {
    const tipo = this.concurso?.tipo?.toString() || ''
    const isRhDta = this.authService.hasPermissionBasedRoles(['RH', 'DTA'])
    const temValorInscricao = this.concurso?.valorInscricao && +this.concurso.valorInscricao !== 0

    this.tabs = [
      { label: 'Resumo', icon: 'summarize', route: 'resumo', visible: true, tooltip: 'Visão geral do concurso' },
      { label: 'Dados', icon: 'edit', route: 'dados', visible: true, tooltip: 'Cadastro e edição do concurso' },
      { label: 'Provas', icon: 'assignment', route: 'provas', visible: this.tiposComProvas.includes(tipo), tooltip: 'Gerenciar provas e critérios' },
      { label: 'Examinadores', icon: 'groups', route: 'examinadores', visible: true, tooltip: 'Comissão julgadora' },
      { label: 'Andamentos', icon: 'list', route: 'andamentos', visible: isRhDta, tooltip: 'Eventos do ciclo de vida' },
      { label: 'Inscrições', icon: 'how_to_reg', route: 'inscricoes', visible: true, tooltip: 'Lista de inscrições' },
      { label: 'Conferência', icon: 'fact_check', route: 'conferencia', visible: true, tooltip: 'Status da documentação' },
      { label: 'Situação', icon: 'thumbs_up_down', route: 'situacao', visible: isRhDta, tooltip: 'Deferimento e indeferimento' },
      { label: 'Documentos', icon: 'description', route: 'documentos', visible: isRhDta, tooltip: 'Documentos das inscrições' },
      { label: 'Pagamento', icon: 'payment', route: 'pagamento', visible: !!temValorInscricao, tooltip: 'Controle de pagamentos' },
      { label: 'Redução', icon: 'discount', route: 'reducao', visible: !!temValorInscricao, tooltip: 'Pedidos de redução de taxa' },
      { label: 'Classificação', icon: 'leaderboard', route: 'classificacao', visible: true, tooltip: 'Resultado e classificação' },
      { label: 'Presença', icon: 'checklist', route: 'presenca', visible: true, tooltip: 'Lista de presença' },
      { label: 'Processo Digital', icon: 'cloud_upload', route: 'processo-digital', visible: true, tooltip: 'Integração SIGAD' },
      { label: 'Modelos', icon: 'auto_fix_high', route: 'modelos', visible: isRhDta || this.authService.hasPermissionBasedRoles(['SG']), tooltip: 'Assistente de geração de documentos' },
    ]
  }
}
